// @ts-nocheck
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useEventDivisionGuard } from "@/hooks/useEventDivisionGuard";

/**
 * Warns operators when a PUBLISHED event has no registerable division —
 * a live event nobody can register for. Also warns when the divisions read
 * failed or looks filtered, so the operator finds out before a coach does.
 */
export function NoDivisionsBanner({
  eventId,
  status,
  className = "",
}: {
  eventId?: string | null;
  status?: string | null;
  className?: string;
}) {
  const { loading, hasDivisions, unreadable, readError } =
    useEventDivisionGuard(eventId);

  if (!eventId || loading) return null;
  if (status !== "published") return null;
  if (hasDivisions) return null;

  const editLink = `/admin-dashboard/events/${eventId}/edit?step=registration`;

  if (unreadable) {
    return (
      <div
        className={`flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm ${className}`}
      >
        <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold text-amber-700">
            We couldn't read this event's divisions.
          </p>
          <p className="mt-1 text-muted-foreground">
            Registration was left open so coaches aren't blocked, but the
            division list came back empty or errored
            {readError ? ` (${readError})` : ""}. Check the divisions setup and
            access rules.
          </p>
          <Link
            to={editLink}
            className="mt-1 inline-block font-medium underline underline-offset-2"
          >
            Review divisions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm ${className}`}
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
      <div>
        <p className="font-semibold text-destructive">
          This event is published but has no divisions. Nobody can register.
        </p>
        <Link
          to={editLink}
          className="mt-1 inline-block font-medium underline underline-offset-2"
        >
          Add divisions now
        </Link>
      </div>
    </div>
  );
}

export default NoDivisionsBanner;
